import h from "./html";

export type ColumnType = ArrayBuffer | string | number | null;

export interface DbConnection {
  postMessage: (msg: any) => void;
  addEventListener: (
    type: "message",
    cb: (ev: MessageEvent<any>) => void,
  ) => void;
}

function randomInt() {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
}

/// Reactive database.
export default class Db {
  private worker: h.State<[DbConnection]>;

  constructor(worker: DbConnection) {
    this.worker = h.state([worker]);
    this.worker.val[0].addEventListener("message", (ev) => {
      if (ev.data === "refresh") {
        this.worker.val = [this.worker.val[0]];
      } else if (ev.data.type === "response") {
        if (ev.data.result) {
          this.responses.get(ev.data.id)?.[0](ev.data.result);
        } else if (ev.data.err != null) {
          this.responses.get(ev.data.id)?.[1](ev.data.result);
        }
        this.responses.delete(ev.data.id);
      }
    });
  }

  private responses: Map<number, [(res: any) => void, (rej: any) => void]> =
    new Map();

  private async sendQuery(
    query: string,
    ...args: Array<ColumnType>
  ): Promise<Array<Array<ColumnType>>> {
    const worker = this.worker.val[0];

    return new Promise((resolve, reject) => {
      const id = randomInt();
      this.responses.set(id, [resolve, reject]);

      worker.postMessage({
        type: "query",
        query,
        args,
        id,
      });
    });
  }

  query<Column extends string>(
    query: string,
    columnNames: Array<Column>,
    args: Array<ColumnType> | (() => Array<ColumnType>),
  ): h.State<"loading" | "error" | Array<{ [K in Column]: ColumnType }>> {
    const state = h.state<
      "loading" | "error" | Array<{ [K in Column]: ColumnType }>
    >("loading");

    h.derive(() => {
      const resolvedArgs = typeof args === "function" ? args() : args;
      this.sendQuery(query, ...resolvedArgs)
        .then((result) => {
          state.val = result.map(
            (columns: Array<ColumnType>) =>
              Object.fromEntries(
                columns.map((c, i) => [columnNames[i], c]),
              ) as {
                [K in Column]: ColumnType;
              },
          );
        })
        .catch((err) => {
          state.val = "error";
        });
    });

    return state;
  }

  async mutate(
    query: string,
    ...args: Array<ColumnType>
  ): Promise<Array<Array<ColumnType>>> {
    const result = await this.sendQuery(query, ...args);
    this.worker.val = [this.worker.val[0]];
    return result;
  }

  async login(email: string, password: string): Promise<number> {
    const worker = this.worker.val[0];

    return new Promise((resolve, reject) => {
      const id = randomInt();
      this.responses.set(id, [resolve, reject]);

      worker.postMessage({
        type: "login",
        email,
        password,
        id,
      });
    });
  }

  async reset(resetCode: string, password: string): Promise<number> {
    const worker = this.worker.val[0];

    return new Promise((resolve, reject) => {
      const id = randomInt();
      this.responses.set(id, [resolve, reject]);

      worker.postMessage({
        type: "reset",
        resetCode,
        password,
        id,
      });
    });
  }

  async connect(email: string): Promise<number> {
    const worker = this.worker.val[0];

    return new Promise((resolve, reject) => {
      const id = randomInt();
      this.responses.set(id, [resolve, reject]);

      worker.postMessage({
        type: "connect",
        email,
        id,
      });
    });
  }

  async disconnect(emailOrUserId: string): Promise<number> {
    const worker = this.worker.val[0];

    return new Promise((resolve, reject) => {
      const id = randomInt();
      this.responses.set(id, [resolve, reject]);

      worker.postMessage({
        type: "disconnect",
        email: emailOrUserId,
        id,
      });
    });
  }
}
